import { useTranslation } from 'react-i18next';
import { useRef, useEffect, useState } from 'react';

export default function Services() {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const isScrollingRef = useRef(false);
  
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Intersection Observer for fade-in effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animate mobile cards when section becomes visible
  useEffect(() => {
    if (isVisible) {
      services.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, index]);
        }, index * 150);
      });
    }
  }, [isVisible]);

  const services = [
    {
      title: t('services.service1Title'),
      description: t('services.service1Desc'),
      image: 'https://public.readdy.ai/ai/img_res/a5d2ef4946b76c92fd8e1d8ef9e3bedf.jpg',
      icon: 'ri-calendar-check-line',
      features: [
        t('services.service1Feature1'),
        t('services.service1Feature2'),
        t('services.service1Feature3'),
        t('services.service1Feature4')
      ]
    },
    {
      title: t('services.service2Title'),
      description: t('services.service2Desc'),
      image: 'https://readdy.ai/api/search-image?query=General%20commercial%20environment%20cleaning%20service%20worker%20cleaning%20office%20meeting%20room%20common%20areas%20restrooms%20professional%20cleaning%20supplies%20bright%20modern%20workspace%20high%20quality%20photography%20clean%20minimalist%20background&width=600&height=400&seq=102&orientation=landscape',
      icon: 'ri-building-2-line',
      features: [
        t('services.service2Feature1'),
        t('services.service2Feature2'),
        t('services.service2Feature3'),
        t('services.service2Feature4')
      ]
    },
    {
      title: t('services.service3Title'),
      description: t('services.service3Desc'),
      image: 'https://readdy.ai/api/search-image?query=Professional%20window%20glass%20cleaning%20service%20worker%20polishing%20large%20office%20windows%20squeegee%20equipment%20sparkling%20clean%20surfaces%20bright%20daylight%20modern%20commercial%20building%20high%20quality%20photography%20clean%20background&width=600&height=400&seq=103&orientation=landscape',
      icon: 'ri-contrast-drop-2-line',
      features: [
        t('services.service3Feature1'),
        t('services.service3Feature2'),
        t('services.service3Feature3'),
        t('services.service3Feature4')
      ]
    },
    {
      title: t('services.service4Title'),
      description: t('services.service4Desc'),
      image: 'https://readdy.ai/api/search-image?query=Deep%20heavy%20cleaning%20service%20professional%20scrubbing%20floors%20walls%20baseboards%20doors%20industrial%20cleaning%20equipment%20thorough%20detailed%20work%20commercial%20space%20bright%20lighting%20high%20quality%20photography%20clean%20minimalist%20background&width=600&height=400&seq=104&orientation=landscape',
      icon: 'ri-sparkling-2-line',
      features: [
        t('services.service4Feature1'),
        t('services.service4Feature2'),
        t('services.service4Feature3'),
        t('services.service4Feature4')
      ]
    },
    {
      title: t('services.service5Title'),
      description: t('services.service5Desc'),
      image: 'https://readdy.ai/api/search-image?query=Post%20construction%20renovation%20cleaning%20crew%20removing%20debris%20fine%20dust%20from%20newly%20built%20commercial%20space%20professional%20cleaning%20equipment%20construction%20site%20cleanup%20bright%20environment%20high%20quality%20photography%20clean%20background&width=600&height=400&seq=105&orientation=landscape',
      icon: 'ri-hammer-line',
      features: [
        t('services.service5Feature1'),
        t('services.service5Feature2'),
        t('services.service5Feature3'),
        t('services.service5Feature4')
      ]
    },
    {
      title: t('services.service6Title'),
      description: t('services.service6Desc'),
      image: 'https://readdy.ai/api/search-image?query=Commercial%20janitorial%20worker%20emptying%20office%20trash%20bins%20into%20large%20wheeled%20garbage%20container%20wearing%20gloves%20and%20uniform%20in%20modern%20office%20building%20hallway%20recycling%20bins%20nearby%20professional%20waste%20management%20service%20realistic%20photography%20bright%20lighting&width=600&height=400&seq=306&orientation=landscape',
      icon: 'ri-delete-bin-line',
      features: [
        t('services.service6Feature1'),
        t('services.service6Feature2'),
        t('services.service6Feature3'),
        t('services.service6Feature4')
      ]
    },
    {
      title: t('services.service7Title'),
      description: t('services.service7Desc'),
      image: 'https://readdy.ai/api/search-image?query=Material%20replenishment%20service%20worker%20restocking%20restroom%20supplies%20paper%20towels%20soap%20dispensers%20cleaning%20products%20in%20commercial%20building%20organized%20storage%20professional%20service%20bright%20clean%20environment%20high%20quality%20photography%20minimalist%20background&width=600&height=400&seq=107&orientation=landscape',
      icon: 'ri-inbox-line',
      features: [
        t('services.service7Feature1'),
        t('services.service7Feature2'),
        t('services.service7Feature3'),
        t('services.service7Feature4')
      ]
    },
    {
      title: t('services.service8Title'),
      description: t('services.service8Desc'),
      image: 'https://readdy.ai/api/search-image?query=Specialized%20detailed%20cleaning%20service%20for%20sensitive%20areas%20professional%20worker%20with%20protective%20equipment%20high%20standard%20sanitization%20medical%20grade%20cleaning%20commercial%20space%20bright%20sterile%20environment%20high%20quality%20photography%20clean%20minimalist%20background&width=600&height=400&seq=108&orientation=landscape',
      icon: 'ri-shield-check-line',
      features: [
        t('services.service8Feature1'),
        t('services.service8Feature2'),
        t('services.service8Feature3'),
        t('services.service8Feature4')
      ]
    },
    {
      title: t('services.service9Title'),
      description: t('services.service9Desc'),
      image: 'https://readdy.ai/api/search-image?query=Space%20preparation%20service%20team%20organizing%20sanitizing%20new%20commercial%20venue%20for%20grand%20opening%20event%20professional%20cleaning%20setup%20immaculate%20presentation%20bright%20elegant%20environment%20high%20quality%20photography%20clean%20minimalist%20background&width=600&height=400&seq=109&orientation=landscape',
      icon: 'ri-store-2-line',
      features: [
        t('services.service9Feature1'),
        t('services.service9Feature2'),
        t('services.service9Feature3'),
        t('services.service9Feature4')
      ]
    }
  ];

  const totalServices = services.length;

  const handleManualScroll = (direction: 'left' | 'right') => {
    // Prevent multiple clicks while scrolling
    if (isScrollingRef.current) return;
    
    isScrollingRef.current = true;
    setIsPaused(true);
    
    // Clear existing timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    // Resume auto-scroll after 10 seconds
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 10000);

    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = 288 + 16; // card width + gap
      
      let newIndex = currentIndex;
      if (direction === 'left') {
        newIndex = currentIndex <= 0 ? totalServices - 1 : currentIndex - 1;
      } else {
        newIndex = currentIndex >= totalServices - 1 ? 0 : currentIndex + 1;
      }
      
      setCurrentIndex(newIndex);
      container.scrollTo({ left: newIndex * cardWidth, behavior: 'smooth' });
      
      // Allow next click after scroll animation completes
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 500);
    } else {
      isScrollingRef.current = false;
    }
  };

  // Auto-scroll with infinite loop
  useEffect(() => {
    if (isPaused) return;
    
    const container = scrollContainerRef.current;
    if (!container) return;

    const autoScroll = setInterval(() => {
      const cardWidth = 288 + 16; // card width + gap
      let nextIndex = currentIndex + 1;
      
      if (nextIndex >= totalServices) {
        nextIndex = 0;
      }
      
      setCurrentIndex(nextIndex);
      container.scrollTo({ left: nextIndex * cardWidth, behavior: 'smooth' });
    }, 5000);

    return () => clearInterval(autoScroll);
  }, [isPaused, currentIndex, totalServices]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  const benefits = [
    {
      icon: 'ri-calendar-schedule-line',
      title: t('services.benefit1Title'),
      description: t('services.benefit1Desc')
    },
    {
      icon: 'ri-file-text-line',
      title: t('services.benefit2Title'),
      description: t('services.benefit2Desc')
    },
    {
      icon: 'ri-settings-3-line',
      title: t('services.benefit3Title'),
      description: t('services.benefit3Desc')
    },
    {
      icon: 'ri-shield-check-line',
      title: t('services.benefit4Title'),
      description: t('services.benefit4Desc')
    }
  ];

  return (
    <section 
      ref={sectionRef}
      id="services" 
      className={`py-12 md:py-24 bg-gradient-to-b from-white to-gray-50 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className={`text-center max-w-3xl mx-auto mb-10 md:mb-20 transition-all duration-500 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <span className="inline-block px-3 py-1 md:px-4 md:py-1.5 bg-[#0F172A]/10 text-[#0F172A] text-xs md:text-sm font-medium rounded-full mb-3 md:mb-4">
            {t('services.badge')}
          </span>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-3 md:mb-6">
            {t('services.title')}
          </h2>
          <p className="text-gray-600 text-sm md:text-lg">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Mobile: Horizontal scroll cards with navigation arrows */}
        <div className="md:hidden relative">
          {/* Left Arrow */}
          <button 
            onClick={() => handleManualScroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
          >
            <i className="ri-arrow-left-s-line text-lg text-[#0F172A]"></i>
          </button>
          
          {/* Right Arrow */}
          <button 
            onClick={() => handleManualScroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
          >
            <i className="ri-arrow-right-s-line text-lg text-[#0F172A]"></i>
          </button>

          <div 
            ref={scrollContainerRef}
            className="overflow-x-auto pb-4 px-6 scrollbar-hide scroll-smooth"
          >
            <div className="flex gap-4" style={{ width: 'max-content' }}>
              {services.map((service, index) => (
                <div 
                  key={index} 
                  className={`w-72 flex-shrink-0 bg-white rounded-xl overflow-hidden shadow-lg transition-all duration-500 ${
                    visibleCards.includes(index) 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-6'
                  }`}
                >
                  <div className="relative h-32 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 to-transparent"></div>
                    <div className="absolute bottom-3 left-3 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center">
                        <i className={`${service.icon} text-sm text-white`}></i>
                      </div>
                      <h3 className="text-sm font-bold text-white">{service.title}</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-600 text-xs mb-3 line-clamp-2">{service.description}</p>
                    <ul className="space-y-1">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-xs text-gray-700">
                          <div className="w-1 h-1 rounded-full bg-[#3B82F6]"></div>
                          <span className="line-clamp-1">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: Grid with staggered animation */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
          {services.map((service, index) => (
            <div 
              key={index} 
              className={`group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: isVisible ? `${200 + index * 80}ms` : '0ms' }}
            >
              <div className="relative h-44 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center">
                    <i className={`${service.icon} text-lg text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-white">{service.title}</h3>
                </div>
              </div>
              <div className="p-5">
                <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                <ul className="space-y-1.5">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits - Hidden on mobile, shown on desktop */}
        <div className={`hidden md:block bg-[#0F172A] rounded-2xl md:rounded-3xl p-6 md:p-12 mt-8 md:mt-0 transition-all duration-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`} style={{ transitionDelay: isVisible ? '800ms' : '0ms' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-br from-[#3B82F6]/20 to-[#60A5FA]/20 flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <i className={`${benefit.icon} text-xl md:text-3xl text-[#60A5FA]`}></i>
                </div>
                <h4 className="text-sm md:text-lg font-semibold text-white mb-1 md:mb-2">{benefit.title}</h4>
                <p className="text-white/70 text-xs md:text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6 md:mt-10">
            <button onClick={() => scrollToSection('contact')} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors px-6 py-2.5 md:px-8 md:py-3 rounded-md bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] hover:from-[#2563EB] hover:to-[#3B82F6] text-white shadow group cursor-pointer">
              {t('services.cta')}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
